package fr.frux.feature.map

import dev.zacsweers.metro.Inject
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapCameraPosition

/** Cle de la derniere vue. Une seule cle : une vue a moitie restauree n'aurait aucun sens. */
internal const val MAP_LAST_VIEW_KEY = "map_last_view"

/**
 * Memoire de la derniere vue de carte, entre deux lancements.
 *
 * Toute erreur de stockage est avalee, des deux cotes : ne pas retrouver sa vue est un
 * desagrement, ne pas pouvoir ouvrir la carte est une panne. Le stockage echoue pour de vrai -
 * quota localStorage, disque plein, preference ecrite par une version anterieure du format.
 * L'annulation de coroutine n'est pas une erreur de stockage : elle traverse (cf.
 * [runCatchingCancellable]).
 */
@Inject
class MapViewStore(
    private val preferences: AppPreferences,
    // Vue imposée par l'URL d'ouverture (web). Fournisseur à usage unique côté appelant.
    private val initialView: () -> MapCameraPosition? = { null },
) {
    /**
     * Fermeture de la carte de zone vide (spec onboarding de contribution §2). Portée ici, avec la
     * dernière vue, parce que c'est aussi une mémoire de la carte, et parce que le constructeur de
     * [MapViewModel] est au plafond detekt de dix paramètres.
     */
    val emptyAreaDismissals: EmptyAreaDismissStore = EmptyAreaDismissStore(preferences)

    /** Pastille de première session en zone peuplée (lot 3), portée ici pour la même raison. */
    val populatedHints: PopulatedAreaHintStore = PopulatedAreaHintStore(preferences)

    suspend fun load(): MapCameraPosition? {
        // Un lien /carte?lat=&lon= prime sur la dernière vue, et la remplace pour les ouvertures
        // suivantes : sans quoi la vue du lien serait perdue au premier rechargement.
        initialView()?.let { linked ->
            save(linked)
            return linked
        }
        val raw =
            runCatchingCancellable { preferences.getString(MAP_LAST_VIEW_KEY) }
                .onFailure { Logger.e(TAG, "Lecture de la vue memorisee impossible", it) }
                .getOrNull() ?: return null

        val parts = raw.split(SEPARATOR)
        if (parts.size != 3) return null

        val latitude = parts[0].toDoubleOrNull() ?: return null
        val longitude = parts[1].toDoubleOrNull() ?: return null
        val zoom = parts[2].toFloatOrNull() ?: return null

        // Une coordonnee hors bornes viserait un point qui n'existe pas : mieux vaut repartir du
        // repli que d'envoyer la camera nulle part.
        if (latitude !in -90.0..90.0 || longitude !in -180.0..180.0) return null

        return MapCameraPosition(Location(latitude, longitude), zoom)
    }

    suspend fun save(position: MapCameraPosition) {
        val raw =
            listOf(
                position.target.latitude,
                position.target.longitude,
                position.zoom,
            ).joinToString(SEPARATOR.toString())

        runCatchingCancellable { preferences.putString(MAP_LAST_VIEW_KEY, raw) }
            .onFailure { Logger.e(TAG, "Ecriture de la vue impossible", it) }
    }

    suspend fun clear() {
        runCatchingCancellable { preferences.remove(MAP_LAST_VIEW_KEY) }
            .onFailure { Logger.e(TAG, "Purge de la vue memorisee impossible", it) }
    }

    private companion object {
        const val TAG = "MapViewStore"
        const val SEPARATOR = ';'
    }
}
